/**
 * Chart documentation shown behind the info icon on the Engagement dashboard.
 *
 * Keys match the chart link property names in `MopaEngagementComponent`.
 */
export const MopaEngagementChartDocs = {
    med_sess_length_by_day_no: /* HTML */ `<p>
            Median session length (minutes) among sessions played on a given
            day-number since install.
        </p>
        <p>
            <strong>Grain / sample:</strong> one row per session; the median is
            the 50th percentile of <code>duration_minutes</code> over all
            sessions at that day-number (<code
                >APPROX_QUANTILES(…, 1000)[OFFSET(500)]</code
            >). It is <em>not</em> a per-player pre-aggregate - a player with
            many short sessions that day contributes many samples, so heavy
            players pull the median toward their session pattern.
        </p>
        <p>
            Median (not average) resists distortion from a few very long
            sessions. Day-number is days since install.
        </p>
        <p>Source: <code>bi_viz.dashboard_engagement</code>.</p>`,

    avg_no_sessions_by_day_no: /* HTML */ `<p>
            Average number of sessions played per active user on a given
            day-number since install.
        </p>
        <pre>
  total sessions played on that day-number      (numerator)
  ─────────────────────────────────────────
  distinct players active on that day-number    (denominator)</pre
        >
        <p>
            Only players active on that day-number are in the
            <strong>denominator</strong>, so this is "sessions per
            <em>active</em> user that day", not per whole cohort. Day-number is
            days since install.
        </p>
        <p>Source: <code>bi_viz.dashboard_engagement</code>.</p>`,

    med_sess_length_by_date: /* HTML */ `<p>
            Median session length (minutes) among sessions played on a given
            date (or period under date aggregation).
        </p>
        <p>
            <strong>Grain / sample:</strong> one row per session; the median is
            the 50th percentile of <code>duration_minutes</code> over all
            sessions in that date/period (<code
                >APPROX_QUANTILES(…, 1000)[OFFSET(500)]</code
            >). It is <em>not</em> a per-player pre-aggregate - a player with
            many short sessions that day contributes many samples.
        </p>
        <p>
            The calendar-date counterpart of the by-day-number median. Median
            (not average) resists distortion from a few very long sessions.
        </p>
        <p>Source: <code>bi_viz.dashboard_engagement</code>.</p>`,

    avg_no_sessions_by_date: /* HTML */ `<p>
            Average number of sessions played per active user on a given date
            (or period under date aggregation).
        </p>
        <pre>
  total sessions played in the date       (numerator)
  ──────────────────────────────────────────
  distinct players active in that date    (denominator)</pre
        >
        <p>
            The <strong>denominator</strong> is the distinct users of the
            selected aggregation, so under Date aggregation = Month it becomes
            sessions / MAU (week → WAU, day → that day's DAU). This is the
            by-date counterpart of the by-day-number chart, whose denominator is
            instead the players active on a specific day-number since install -
            the two are not the same cut.
        </p>
        <p>Source: <code>bi_viz.dashboard_engagement</code>.</p>`,

    sessions_distribution_by_days_05min: /* HTML */ `<p>
            Histogram of session lengths for the selected day-numbers since
            install (fine 0.5-minute bins).
        </p>
        <p>
            <strong>What's counted:</strong> bar height is the number of
            sessions falling into each 0.5-minute duration bucket (<code
                >COUNT(*)</code
            >
            of sessions in that bin). Each selected day-number is drawn as its
            own series and sessions longer than 10 minutes are cut off.
        </p>
        <p>
            The "Selected Day/Session" setting controls which day-numbers are
            shown.
        </p>
        <p>Source: <code>bi_viz.dashboard_engagement</code>.</p>`,

    sessions_distribution_by_sessions_05min: /* HTML */ `<p>
            Histogram of session lengths for the selected session numbers - 1st,
            2nd, 3rd session, etc. - regardless of the day they happened (fine
            0.5-minute bins).
        </p>
        <p>
            <strong>What's counted:</strong> bar height is the number of
            sessions in each 0.5-minute duration bucket (<code>COUNT(*)</code>),
            split by session ordinal. Sessions longer than 10 minutes are cut
            off.
        </p>
        <p>
            The "Selected Day/Session" setting controls which session numbers
            are shown.
        </p>
        <p>Source: <code>bi_viz.dashboard_engagement</code>.</p>`,

    sessions_distribution_by_days_5min: /* HTML */ `<p>
            Histogram of session lengths for the selected day-numbers since
            install (coarse 5-minute bins).
        </p>
        <p>
            <strong>What's counted:</strong> bar height is the number of
            sessions falling into each 5-minute duration bucket (<code
                >COUNT(*)</code
            >
            of sessions in that bin). each selected day-number is drawn as its
            own series and sessions longer than 120 minutes are cut off.
        </p>
        <p>
            The "Selected Day/Session" setting controls which day-numbers are
            shown.
        </p>
        <p>Source: <code>bi_viz.dashboard_engagement</code>.</p>`,

    sessions_distribution_by_sessions_5min: /* HTML */ `<p>
            Histogram of session lengths for the selected session numbers - 1st,
            2nd, 3rd session, etc. - regardless of the day they happened (coarse
            5-minute bins).
        </p>
        <p>
            <strong>What's counted:</strong> bar height is the number of
            sessions in each 5-minute duration bucket (<code>COUNT(*)</code>),
            split by session ordinal. sessions longer than 120 minutes are cut
            off.
        </p>
        <p>
            The "Selected Day/Session" setting controls which session numbers
            are shown.
        </p>
        <p>Source: <code>bi_viz.dashboard_engagement</code>.</p>`,
} as const;
